import { ContentDto, GetPromptDto } from '@app/modules/content-ai/content-ai.types';
import { useContentAiGenerateAiArticlesContent } from '@mi-front/api-v2';
import CheckIcon from '@mui/icons-material/Check';
import { Box, FormLabel as MuiFormLabel, useTheme } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import ReactSelect, { ValueType } from 'react-select';
import { useContent } from '../../../../hooks/useContent';
import { useContentAiPrompts } from '../../../../hooks/useContentAiPrompts';
import { ContentContainer, CustomTextField, PrimaryButton } from '../../components';

export function ArticlesGenerator({
  content,
  allowInstructions,
  onSuccess,
}: {
  content: ContentDto;
  allowInstructions?: boolean;
  onSuccess?: () => void;
}) {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const [instructions, setInstructions] = useState<string | undefined>();
  const [prompt, setPrompt] = useState<GetPromptDto>();
  const prompts = useContentAiPrompts(content);
  const mutation = useContentAiGenerateAiArticlesContent();
  const query = useContent(content.id);
  const { formatMessage, $t } = useIntl();

  const customSelectStyles = {
    control: (base: object, state: { isFocused: boolean }) => ({
      ...base,
      padding: '8px 12px',
      minHeight: '44px',
      fontFamily: 'Inter, sans-serif',
      fontSize: '16px',
      lineHeight: '28px',
      backgroundColor: theme.palette.background.paper,
      border: state.isFocused ? '1px solid #AF52DE' : `1px solid ${theme.palette.divider}`,
      borderRadius: '6px',
      boxShadow: state.isFocused
        ? '0px 1px 3px rgba(0, 0, 0, 0.08), 0 0 0 2px rgba(175, 82, 222, 0.1)'
        : '0px 1px 3px rgba(0, 0, 0, 0.08)',
      '&:hover': {
        borderColor: state.isFocused ? '#AF52DE' : theme.palette.divider,
      },
    }),
    placeholder: (base: object) => ({
      ...base,
      color: theme.palette.text.secondary,
      fontWeight: '400',
    }),
    singleValue: (base: object) => ({
      ...base,
      color: theme.palette.text.primary,
    }),
    menu: (base: object) => ({
      ...base,
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: '0px 4px 12px rgba(0,0,0,0.3)',
    }),
    menuList: (base: object) => ({
      ...base,
      padding: 0,
    }),
    option: (base: object, state: { isSelected: boolean; isFocused: boolean }) => ({
      ...base,
      backgroundColor: state.isSelected
        ? '#AF52DE'
        : state.isFocused
          ? theme.palette.action.hover
          : 'transparent',
      color: state.isSelected ? '#fff' : theme.palette.text.primary,
      cursor: 'pointer',
    }),
  };

  return (
    <ContentContainer
      sx={{
        justifyContent: 'center',
        alignItems: 'flex-start',
        gap: '64px',
      }}
    >
      {/* Form Fields */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          gap: '24px',
        }}
      >
        {/* Select Prompt */}
        <Box
          sx={{
            width: '366px',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
          }}
        >
          <MuiFormLabel
            sx={{
              color: 'text.primary',
              fontSize: '16px',
              fontFamily: 'Inter',
              fontWeight: 600,
              lineHeight: '28px',
              letterSpacing: '-0.176px',
            }}
          >
            {formatMessage({
              id: 'contentAi.steps.aiArticles.choosePrompt',
              defaultMessage: 'Elige el tono del artículo',
            })}
          </MuiFormLabel>
          <ReactSelect
            options={prompts}
            value={prompt}
            getOptionValue={(l) => l.id}
            getOptionLabel={(l) => l.name}
            onChange={(item: ValueType<GetPromptDto, false>) => {
              setPrompt(item ?? undefined);
            }}
            placeholder={$t({
              id: 'contentAi.placeholder.tone',
              defaultMessage: 'Positivo, negativo, neutro',
            })}
            styles={customSelectStyles}
          />
        </Box>

        {/* Instructions TextArea */}
        {allowInstructions && (
          <Box
            sx={{
              width: '520px',
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
            }}
          >
            <MuiFormLabel
              sx={{
                color: 'text.primary',
                fontSize: '16px',
                fontFamily: 'Inter',
                fontWeight: 600,
                lineHeight: '28px',
                letterSpacing: '-0.176px',
              }}
            >
              <FormattedMessage
                id="contentAi.articles.instructions"
                defaultMessage="Instrucciones"
              />{' '}
              <Box
                component="span"
                sx={{
                  fontSize: '12px',
                  fontWeight: 400,
                  lineHeight: '20px',
                }}
              >
                <FormattedMessage id="common.optional" defaultMessage="(opcional)" />
              </Box>
            </MuiFormLabel>
            <CustomTextField
              placeholder={$t({
                id: 'contentAi.instructionsPlaceholder',
                defaultMessage: 'Ingresa las instrucciones aquí.',
              })}
              onChange={(evt) => {
                setInstructions(evt.target.value);
              }}
              autoFocus
              multiline
              rows={1}
            />
          </Box>
        )}
      </Box>

      {/* Generate Button */}
      <PrimaryButton
        onClick={async () => {
          mutation.mutate(
            {
              id: content.id,
              data: {
                promptId: prompt!.id,
                instructions,
              },
            },
            {
              onSuccess: async () => {
                await queryClient.invalidateQueries({
                  queryKey: query.queryKey,
                });
                onSuccess?.();
              },
            },
          );
        }}
        loading={mutation.isPending}
        startIcon={<CheckIcon />}
        sx={{ padding: '8px 24px', height: '44px' }}
      >
        <FormattedMessage
          id="contentAi.articles.generateContent"
          defaultMessage="Generar contenido"
        />
      </PrimaryButton>
    </ContentContainer>
  );
}
